import { z } from 'zod';
import { formatIsoDate, parseDateInput } from '@/utils/date';

const DEFAULT_MAX_MESSAGE = (max: number) => `Massimo ${max} caratteri`;

export const requiredText = (min: number, max: number, messages?: { min?: string; max?: string }) =>
  z
    .string()
    .trim()
    .min(min, messages?.min ?? `Almeno ${min} caratteri`)
    .max(max, messages?.max ?? DEFAULT_MAX_MESSAGE(max));

export const optionalText = (max: number, message?: string) =>
  z
    .string()
    .trim()
    .max(max, message ?? DEFAULT_MAX_MESSAGE(max))
    .or(z.literal(''))
    .nullish()
    .transform((value) => {
      if (value === '' || value == null) return undefined;
      return value;
    });

export const optionalEmail = (message?: string) =>
  z
    .string()
    .trim()
    .email(message ?? 'Inserisci un indirizzo email valido')
    .or(z.literal(''))
    .nullish()
    .transform((value) => {
      if (value === '' || value == null) return undefined;
      return value;
    });

const normalizeDateString = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const parsed = parseDateInput(trimmed);
  if (!parsed) {
    return undefined;
  }

  return formatIsoDate(parsed);
};

const MIN_YEAR = 1990;
const MAX_YEAR = 2100;

function isYearInRange(year: string | number): boolean {
  const yearNum = typeof year === 'string' ? parseInt(year, 10) : year;
  return !Number.isNaN(yearNum) && yearNum >= MIN_YEAR && yearNum <= MAX_YEAR;
}

const normalizeMonthYearString = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month] = trimmed.split('-');
    if (!isYearInRange(year)) return undefined;
    return `${year}-${month}-01`;
  }

  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const [year, month] = trimmed.split('-');
    if (!isYearInRange(year)) return undefined;
    return `${year}-${month}-01`;
  }

  if (/^\d{2}[/-]\d{4}$/.test(trimmed)) {
    const separator = trimmed.includes('-') ? '-' : '/';
    const [month, year] = trimmed.split(separator);
    if (!isYearInRange(year)) return undefined;
    return `${year}-${month.padStart(2, '0')}-01`;
  }

  if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(trimmed)) {
    const separator = trimmed.includes('-') ? '-' : '/';
    const [, month, year] = trimmed.split(separator);
    if (!isYearInRange(year)) return undefined;
    return `${year}-${month.padStart(2, '0')}-01`;
  }

  const parsed = parseDateInput(trimmed);
  if (!parsed) {
    return undefined;
  }

  if (!isYearInRange(parsed.getFullYear())) {
    return undefined;
  }

  return formatIsoDate(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
};

export const optionalDate = (message?: string) =>
  z
    .string()
    .trim()
    .or(z.literal(''))
    .nullish()
    .superRefine((value, ctx) => {
      if (!value) return;
      if (normalizeDateString(value)) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: message ?? 'Inserisci una data valida (gg/mm/aaaa)',
      });
    })
    .transform((value) => {
      if (!value) return undefined;
      return normalizeDateString(value);
    });

const optionalMonthYear = (message?: string) =>
  z
    .string()
    .trim()
    .or(z.literal(''))
    .nullish()
    .superRefine((value, ctx) => {
      if (!value) return;
      if (normalizeMonthYearString(value)) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: message ?? `Inserisci un mese valido (mm/aaaa, anno ${MIN_YEAR}-${MAX_YEAR})`,
      });
    })
    .transform((value) => {
      if (!value) return undefined;
      return normalizeMonthYearString(value);
    });

export const requiredMonthYear = (message?: string) =>
  optionalMonthYear(message).refine((value) => value !== undefined, {
    message: message ?? `Inserisci un mese valido (mm/aaaa, anno ${MIN_YEAR}-${MAX_YEAR})`,
  });

const normalizeYearString = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Accept 4-digit year directly
  if (/^\d{4}$/.test(trimmed)) {
    if (!isYearInRange(trimmed)) return undefined;
    return trimmed;
  }

  // Accept ISO date format - extract year
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const year = trimmed.split('-')[0];
    if (!isYearInRange(year)) return undefined;
    return year;
  }

  return undefined;
};

const optionalYear = (message?: string) =>
  z
    .string()
    .trim()
    .or(z.literal(''))
    .nullish()
    .superRefine((value, ctx) => {
      if (!value) return;
      if (normalizeYearString(value)) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: message ?? `Inserisci un anno valido (${MIN_YEAR}-${MAX_YEAR})`,
      });
    })
    .transform((value) => {
      if (!value) return undefined;
      return normalizeYearString(value);
    });

export const requiredYear = (message?: string) =>
  optionalYear(message).refine((value) => value !== undefined, {
    message: message ?? `Inserisci un anno valido (${MIN_YEAR}-${MAX_YEAR})`,
  });

export const optionalNumber = (schema: z.ZodNumber) =>
  z.preprocess((value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return undefined;
      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? value : parsed;
    }
    return value;
  }, schema.optional());

/**
 * Required positive integer with sensible error messages.
 * For counts, quantities, etc.
 */
export const requiredPositiveInteger = (message?: string) =>
  z.preprocess(
    (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = parseInt(trimmed, 10);
        return Number.isNaN(parsed) ? value : parsed;
      }
      return value;
    },
    z
      .number({ message: message ?? 'Inserisci un numero valido' })
      .int({ message: message ?? 'Inserisci un numero intero' })
      .positive({ message: message ?? 'Inserisci un numero maggiore di zero' })
  );

/**
 * Optional positive integer with sensible error messages.
 * For optional counts, quantities, etc.
 */
export const optionalPositiveInteger = (message?: string) =>
  z.preprocess(
    (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = parseInt(trimmed, 10);
        return Number.isNaN(parsed) ? value : parsed;
      }
      return value;
    },
    z
      .number({ message: message ?? 'Inserisci un numero valido' })
      .int({ message: message ?? 'Inserisci un numero intero' })
      .positive({ message: message ?? 'Inserisci un numero maggiore di zero' })
      .optional()
  );

/**
 * Non-negative integer (0 or more).
 * For counts that can be zero.
 */
export const optionalNonNegativeInteger = (message?: string) =>
  z.preprocess(
    (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = parseInt(trimmed, 10);
        return Number.isNaN(parsed) ? value : parsed;
      }
      return value;
    },
    z
      .number({ message: message ?? 'Inserisci un numero valido' })
      .int({ message: message ?? 'Inserisci un numero intero' })
      .nonnegative({ message: message ?? 'Inserisci un numero maggiore o uguale a zero' })
      .optional()
  );

/**
 * Numeric field for installation years as numbers (not strings).
 * Validates year is within reasonable range (1900-2100).
 */
export const optionalYearNumber = (messages?: { invalid?: string; range?: string }) =>
  z.preprocess(
    (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = parseInt(trimmed, 10);
        return Number.isNaN(parsed) ? value : parsed;
      }
      return value;
    },
    z
      .number({ message: messages?.invalid ?? 'Inserisci un anno valido' })
      .int({ message: messages?.invalid ?? 'Inserisci un anno valido' })
      .min(1900, { message: messages?.range ?? "L'anno deve essere compreso tra 1900 e 2100" })
      .max(2100, { message: messages?.range ?? "L'anno deve essere compreso tra 1900 e 2100" })
      .optional()
  );

/**
 * Numeric field with custom range validation.
 * Use for any bounded numeric input (e.g., kg amounts, percentages).
 */
export const optionalNumberInRange = (
  min: number,
  max: number,
  messages?: { invalid?: string; min?: string; max?: string }
) =>
  z.preprocess(
    (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = Number(trimmed);
        return Number.isNaN(parsed) ? value : parsed;
      }
      return value;
    },
    z
      .number({ message: messages?.invalid ?? 'Inserisci un numero valido' })
      .min(min, { message: messages?.min ?? `Inserisci un valore minimo di ${min}` })
      .max(max, { message: messages?.max ?? `Inserisci un valore massimo di ${max}` })
      .optional()
  );
